/**
 * LES RÔLES ET LES RÈGLES DE DROIT — la seule autorité, front comme API.
 *
 * L'interface l'appelle pour afficher, l'API pour autoriser. Réécrire une de
 * ces règles dans un composant, c'est la voir diverger du serveur — et
 * découvrir l'écart en recette.
 *
 * Deux axes, qu'on ne mélange pas :
 *  - la NATURE du compte (staff, collaborateur, client) se CALCULE à chaque
 *    requête : elle ne se donne pas, elle se constate (DEC-017) ;
 *  - les RÔLES se DONNENT, dans l'administration, et sont lus en base.
 * La nature borne les rôles : un rôle donné à un compte qui ne peut pas le
 * tenir est ignoré, quoi qu'il y ait en base. C'est ce qui rend impossible
 * qu'un contact client devienne intervenant par une erreur de saisie.
 */
/* ------------------------------------------------------------------ */
/* Nature du compte                                                    */
/* ------------------------------------------------------------------ */
/**
 * - `staff` : salarié d'IT Solutions, connecté par SSO. Seul à pouvoir traiter
 *   les demandes techniques et la facturation.
 * - `collaborateur` : autre salarié du groupe (entités Altonéo), connecté par
 *   SSO. Dépose des demandes ; peut tenir la borne RH, le service RH étant
 *   chez Altonéo.
 * - `client` : contact d'un client externe, compte local activé par lien
 *   (DEC-003). Ne voit que les demandes de sa société.
 */
export const ACCOUNT_KINDS = ['staff', 'collaborateur', 'client'];
export const ACCOUNT_KIND_LABELS = {
    staff: 'Staff IT Solutions',
    collaborateur: 'Collaborateur du groupe',
    client: 'Client',
};
/**
 * LA NATURE D'UN COMPTE (DEC-017, élargie par DEC-030).
 *
 * - Une adresse d'un domaine du tenant (it-solutions.cloud, altoneo.com) est
 *   celle d'un salarié du groupe. Elle est STAFF :
 *     · si elle est du domaine d'IT Solutions (it-solutions.cloud), qui
 *       n'appartient qu'à elle ;
 *     · ou si elle est celle d'un contact actif de l'entité IT Solutions dans
 *       `core` — la liste que tient Youzer —, pour les salariés d'IT Solutions
 *       qui écrivent depuis altoneo.com.
 *   COLLABORATEUR sinon.
 * - Toute autre adresse est celle d'un CLIENT.
 *
 * DEC-030 : le contact seul laissait « collaborateur » un salarié d'IT
 * Solutions qui se connecte en @it-solutions.cloud quand sa fiche `core` porte
 * son adresse @altoneo.com (relevé le 07/10/2026). Toujours écarté : un groupe
 * Entra dédié (une liste de plus à tenir à jour, à la main, hors de la borne).
 *
 * DEC-034 : le domaine ne suffit plus quand l'annuaire dit le contraire. Une
 * adresse @it-solutions.cloud dont le contact de l'entité IT Solutions est
 * ARCHIVÉ (départ tenu par Youzer), sans autre contact actif, n'est plus staff
 * — Lucie BOULARD restait proposée comme intervenante (13/10/2026). Une adresse
 * sans aucun contact (le cas d'Eric AUBRY) reste staff par son domaine.
 */
export function accountKindOf(input) {
    if (!input.tenantEmail)
        return 'client';
    if (input.staffEntityContact)
        return 'staff';
    return input.staffDomainEmail && !input.staffEntityContactArchived ? 'staff' : 'collaborateur';
}
/**
 * UNE PHOTO DE PROFIL se lit chez Microsoft pour les comptes qui y entrent :
 * le staff et les collaborateurs du groupe (DEC-037). Un client n'entre que par
 * e-mail : il n'a que ses initiales. L'API garde la même règle ; l'écran la lit
 * pour savoir s'il vaut la peine de demander la photo.
 */
export function photoMicrosoft(kind) {
    return kind === 'staff' || kind === 'collaborateur';
}
export function isTenantEmail(email, tenantDomains) {
    const domain = email.trim().toLowerCase().split('@')[1] ?? '';
    return tenantDomains.some((d) => d.trim().toLowerCase() === domain);
}
/**
 * VERROU : un salarié du groupe n'entre QUE par Microsoft — un mot de passe
 * créé à côté contournerait le MFA et le départ géré dans Entra. Un client
 * n'entre que par e-mail : il n'est pas dans notre tenant. Rend le refus,
 * formulé pour être compris par celui qui le lit, ou null si l'entrée est admise.
 */
export function loginRefusal(kind, provider) {
    if (kind !== 'client' && provider !== 'azure') {
        return 'Les comptes du groupe se connectent avec Microsoft. Utilisez le bouton « Se connecter avec Microsoft ».';
    }
    if (kind === 'client' && provider !== 'email') {
        return "Cette adresse n'appartient pas au groupe : connectez-vous avec l'e-mail et le mot de passe de votre accès client.";
    }
    return null;
}
/* ------------------------------------------------------------------ */
/* Rôles (CDC §4.3)                                                    */
/* ------------------------------------------------------------------ */
export const ROLES = [
    'demandeur',
    'demandeur_rh',
    'intervenant',
    'facturation',
    'intervenant_rh',
    'administrateur',
    'supervision',
];
export const ROLE_LABELS = {
    demandeur: 'Demandeur',
    demandeur_rh: 'Demandeur RH',
    intervenant: 'Intervenant',
    facturation: 'Facturation',
    intervenant_rh: 'Intervenant RH',
    administrateur: 'Administrateur',
    supervision: 'Supervision',
};
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
/**
 * Ce que chaque nature de compte PEUT tenir. `demandeur` est de droit pour
 * tous : tout compte actif rattaché à une société dépose des demandes.
 * `demandeur_rh` est de droit pour les salariés du groupe (CDC §4.3).
 */
const ALLOWED = {
    staff: ROLES,
    collaborateur: ['demandeur', 'demandeur_rh', 'intervenant_rh'],
    client: ['demandeur'],
};
const AUTOMATIC = {
    staff: ['demandeur', 'demandeur_rh'],
    collaborateur: ['demandeur', 'demandeur_rh'],
    client: ['demandeur'],
};
export function canHoldRole(kind, role) {
    return ALLOWED[kind].includes(role);
}
/**
 * Rôles EFFECTIFS : les rôles automatiques de la nature, plus les rôles donnés
 * qu'elle peut tenir. Le reste est ignoré — jamais une erreur, car un compte
 * peut changer de nature (départ d'IT Solutions vers une entité Altonéo) sans
 * qu'on ait encore retiré ses rôles.
 */
export function effectiveRoles(kind, granted) {
    const out = new Set(AUTOMATIC[kind]);
    for (const r of granted)
        if (isRole(r) && canHoldRole(kind, r))
            out.add(r);
    return ROLES.filter((r) => out.has(r));
}
/** Rôles attribuables depuis l'administration, pour une nature donnée. */
export function assignableRoles(kind) {
    return ALLOWED[kind].filter((r) => !AUTOMATIC[kind].includes(r));
}
const has = (roles, ...allowed) => roles.some((r) => allowed.includes(r));
/* ------------------------------------------------------------------ */
/* Règles                                                              */
/* ------------------------------------------------------------------ */
/** Administre l'outil : comptes, rôles, référentiels, informations, audit. */
export function canAdminister(roles) {
    return has(roles, 'administrateur');
}
/** Voit et traite la file technique, saisit des interventions. */
export function canHandleRequests(roles) {
    return has(roles, 'intervenant', 'administrateur');
}
/** Consulte les clients et leurs contacts. */
export function canViewClients(roles) {
    return has(roles, 'intervenant', 'facturation', 'administrateur');
}
/** Pré-facturation, imputation Ekivox, relevés. */
export function canBill(roles) {
    return has(roles, 'facturation');
}
/** Dépose une demande technique pour sa société. */
export function canRequest(roles) {
    return has(roles, 'demandeur');
}
/** Consulte les indicateurs (accueil et pilotage). */
export function canViewIndicators(roles) {
    return has(roles, 'intervenant', 'facturation', 'administrateur', 'supervision');
}
/**
 * Accède à la borne RH en tant que service RH. L'administrateur n'y entre PAS
 * (règle 14 : le contenu RH ne sort jamais de son cloisonnement).
 */
export function canHandleHr(roles) {
    return has(roles, 'intervenant_rh');
}
/**
 * Arrive sur le tableau de bord de l'équipe à la connexion, et seul à pouvoir
 * le lire : le staff d'IT Solutions (DEC-028). Par la NATURE, et non par le
 * domaine @it-solutions.cloud : DEC-017 a écarté le domaine seul, des salariés
 * d'IT Solutions écrivant depuis altoneo.com.
 */
export function peutVoirTableauDeBord(kind) {
    return kind === 'staff';
}
/* ------------------------------------------------------------------ */
/* Fusion de comptes (DEC-030)                                         */
/* ------------------------------------------------------------------ */
/**
 * FUSIONNER DEUX COMPTES D'UNE MÊME PERSONNE : un salarié qui a deux adresses
 * du groupe (eaubry@altoneo.com et eaubry@it-solutions.cloud) a eu deux
 * comptes — l'un repris de l'ancienne borne, l'autre ouvert à sa connexion.
 * Tout ce que portait le premier passe sur le second, qui reçoit aussi ses
 * rôles ; le premier est désactivé, jamais supprimé (il reste l'auteur des
 * traces passées : journal, relevés envoyés, régularisations).
 *
 * Rend le refus, formulé pour celui qui le lit (règle 9), ou null.
 */
export function refusFusionComptes(source, cible, moiId) {
    if (source.id === cible.id)
        return 'Choisissez un autre compte : on ne fusionne pas un compte avec lui-même.';
    if (source.id === moiId)
        return 'Vous ne pouvez pas fusionner votre propre compte. Demandez-le à un autre administrateur.';
    if (source.kind === 'client' || cible.kind === 'client') {
        return 'On ne fusionne que les comptes de salariés du groupe : un compte client est celui d’une autre personne.';
    }
    if (!cible.active) {
        return 'Le compte de destination est désactivé : choisissez celui avec lequel la personne se connecte, ou réactivez-le d’abord.';
    }
    return null;
}
/** Les rôles du compte fusionné : ceux des deux comptes, que la nature de la destination peut tenir. */
export function rolesApresFusion(source, cible, kindCible) {
    const tous = new Set([...source, ...cible].filter(isRole));
    return ROLES.filter((r) => tous.has(r) && canHoldRole(kindCible, r) && !AUTOMATIC[kindCible].includes(r));
}
//# sourceMappingURL=roles.js.map